// SignierlinkDialog.tsx — eigenes Popup für erzeugte Signierlinks, statt
// `alert`/Zwischenablage (User 28.09.2026). Bewusst OHNE Fluent-Dialog: die
// Popup-Schale der Kontaktakte `.kvp-rd.kvp-rd-scrim > .dm`
// (`redesign/contactRedesign.css`), per Portal an `document.body` mit eigener
// `.kvp-rd`-Hülle, damit die Farbvariablen auch außerhalb der Akte gelten.
//
// Je Link: Titel der Vollmacht, der Link lesbar und markierbar, „Link
// kopieren" (→ „Kopiert"), „Im neuen Fenster öffnen", gültig bis. Esc, ✕,
// „Schließen" oder ein Klick daneben schließen.
//
// `zeigeSignierlinkDialog` hängt das Popup unabhängig vom Aufrufer ein — für
// die Kontaktanlage, deren Formular nach dem Speichern verschwindet.

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { createRoot } from "react-dom/client";
import "../redesign/contactRedesign.css";
import { gueltigBisText, linkInNeuemFenster, type SignierlinkAnzeige } from "./signierlink";
import { aktivesDokument, alleDokumente } from "../windowManager/browserFenster/fensterRegister";
import { BrowserFensterHuelle } from "../windowManager/browserFenster/BrowserFensterHuelle";

type Props = {
  links: SignierlinkAnzeige[];
  /** Weitere Hinweise (z. B. Fehler aus der Kontaktanlage). */
  hinweise?: string[];
  ueberschrift?: string;
  onClose: () => void;
};

async function kopieren(text: string, feld: HTMLInputElement | null): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* Fallback unten */
  }
  if (!feld) return false;
  feld.focus();
  feld.select();
  try {
    return aktivesDokument().execCommand("copy");
  } catch {
    return false;
  }
}

const knopf: React.CSSProperties = {
  appearance: "none",
  border: "1.5px solid #ddd3dc",
  background: "#fff",
  color: "#591149",
  cursor: "pointer",
  font: '700 13.5px/1 "Hanken Grotesk", sans-serif',
  padding: "9px 15px",
  borderRadius: 10,
};
const primaer: React.CSSProperties = { ...knopf, background: "#7d2169", borderColor: "#7d2169", color: "#fff" };

const LinkZeile: React.FC<{ link: SignierlinkAnzeige; autoFokus: boolean }> = ({ link, autoFokus }) => {
  const feldRef = React.useRef<HTMLInputElement | null>(null);
  const [kopiert, setKopiert] = React.useState(false);
  React.useEffect(() => {
    if (!kopiert) return;
    const t = setTimeout(() => setKopiert(false), 2000);
    return () => clearTimeout(t);
  }, [kopiert]);
  const bis = gueltigBisText(link.expiresAt);
  return (
    <div data-testid="signierlink-zeile" style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 18 }}>
      <div style={{ font: '700 15px/1.3 "Hanken Grotesk", sans-serif', color: "#241522" }}>{link.titel}</div>
      <input
        ref={feldRef}
        readOnly
        value={link.url}
        aria-label={`Signierlink ${link.titel}`}
        onFocus={(e) => e.currentTarget.select()}
        style={{
          width: "100%",
          boxSizing: "border-box",
          height: 40,
          border: "1.5px solid #ddd3dc",
          borderRadius: 9,
          padding: "0 12px",
          font: '500 14px/1 "Hanken Grotesk", sans-serif',
          color: "#241522",
          background: "#f8f4f8",
        }}
      />
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <button
          type="button"
          style={primaer}
          autoFocus={autoFokus}
          onClick={async () => setKopiert(await kopieren(link.url, feldRef.current))}
        >
          {kopiert ? "✓ Kopiert" : "Link kopieren"}
        </button>
        <button type="button" style={knopf} onClick={() => linkInNeuemFenster(link.url)}>
          Im neuen Fenster öffnen
        </button>
        {bis && <span style={{ font: '500 13px/1.3 "Hanken Grotesk", sans-serif', color: "#6b5a66" }}>{bis}</span>}
      </div>
    </div>
  );
};

export const SignierlinkDialog: React.FC<Props> = ({ links, hinweise, ueberschrift, onClose }) => {
  React.useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    alleDokumente.addEventListener("keydown", taste, true);
    return () => alleDokumente.removeEventListener("keydown", taste, true);
  }, [onClose]);

  const titel = ueberschrift ?? (links.length === 1 ? "Link zum Unterschreiben" : "Links zum Unterschreiben");
  return createPortal(
    // z-index über Fluent-Portalen: das Popup kann aus einem Menü oder der
    // Kontaktanlage heraus kommen.
    <div className="kvp-rd kvp-rd-scrim" style={{ zIndex: 20000 }} onClick={onClose} data-testid="signierlink-scrim">
      <div
        className="dm"
        role="dialog"
        aria-modal="true"
        aria-label={titel}
        style={{ width: "min(620px, 100%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dm-head">
          <div className="dm-head-top">
            <div className="dm-titles">
              <div className="dm-kicker">Vollmachten</div>
              <h2>{titel}</h2>
            </div>
            <button type="button" className="dm-x" aria-label="Fenster schließen" onClick={onClose}>
              ✕
            </button>
          </div>
        </div>
        <div className="dm-body" style={{ paddingTop: 6 }}>
          {links.length > 0 && (
            <p style={{ margin: "0 0 16px", font: '500 14px/1.45 "Hanken Grotesk", sans-serif', color: "#6b5a66" }}>
              Über diesen Link kann der Kunde die Vollmacht ohne Anmeldung ansehen und unterschreiben.
            </p>
          )}
          {links.map((l, i) => (
            <LinkZeile key={l.url} link={l} autoFokus={i === 0} />
          ))}
          {hinweise && hinweise.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18, font: '500 14px/1.5 "Hanken Grotesk", sans-serif', color: "#241522" }}>
              {hinweise.map((h, i) => (
                <li key={i}>{h}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="dm-foot">
          <button type="button" style={knopf} autoFocus={links.length === 0} onClick={onClose}>
            Schließen
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

/** Popup ohne umgebende Komponente zeigen; erfüllt sich beim Schließen. */
export function zeigeSignierlinkDialog(props: Omit<Props, "onClose">): Promise<void> {
  return new Promise((resolve) => {
    const dok = aktivesDokument();
    const host = dok.createElement("div");
    dok.body.appendChild(host);
    const root = createRoot(host);
    let zu = false;
    const schliessen = () => {
      if (zu) return;
      zu = true;
      // Nicht synchron aus dem eigenen Event-Handler heraus aushängen.
      setTimeout(() => {
        root.unmount();
        host.remove();
      }, 0);
      resolve();
    };
    const dialog = <SignierlinkDialog {...props} onClose={schliessen} />;
    root.render(dok === document ? dialog : <BrowserFensterHuelle dok={dok}>{dialog}</BrowserFensterHuelle>);
  });
}

export default SignierlinkDialog;
